import cn from "classnames"
import { useId } from "react"

import { Button, Flex } from "@/atoms"
import { getLastStatusChange, isIssueResolved } from "@/domain/issue"
import {
  DEFAULT_LONG_FORMAT,
  getDateFromSeconds,
  getDateInUserLocale,
  getDiffToNow
} from "@/helpers/date.helpers"
import { useToggle } from "@/hooks/useToggle"
import { useTranslation } from "@/hooks/useTranslation"
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  InboxOutlined
} from "@/icons"
import { ResolvedContent } from "@/molecules/IssueStatusTag"
import { DismissedMeta } from "./DismissedMeta"
import { StatusChangeRow } from "./StatusChangeRow"

import alertStyles from "../IssueStatusAlert.module.scss"
import styles from "./StatusBar.module.scss"

import type { StatusBarRecord } from "@/molecules/IssueStatusAlert"

// The current status only; "Show changes" opens its last change. No "Reopened" status.
export const StatusBar = (props: { record: StatusBarRecord }) => {
  const { record } = props
  const { status, discoveredAt } = record
  const { t } = useTranslation()
  const { value: collapsed, toggle } = useToggle(true)
  const changeRowId = useId()
  const isOpen = status === "open"

  if (!isOpen && status !== "dismissed" && status !== "dismissed_resolved")
    return null

  const withResolved = !isOpen && isIssueResolved(record)
  const change = getLastStatusChange(record)
  const discoveredDate = getDateFromSeconds(discoveredAt)

  return (
    <Flex
      vertical
      className={cn(styles.bar, !collapsed && styles.expanded)}
      data-id="issue-status-bar"
    >
      <Flex wrap align="center" justify="space-between" gap={12}>
        <Flex align="center" gap={8} className={styles.labels}>
          {isOpen ? (
            <span className={cn(styles.label, styles.labelOpen)}>
              <InboxOutlined aria-hidden className={styles.labelIcon} />
              {t("general.open")}
            </span>
          ) : (
            <span className={styles.label}>
              <CloseCircleOutlined aria-hidden className={styles.labelIcon} />
              {t("general.dismissed")}
            </span>
          )}
          {withResolved && (
            <>
              <span className={styles.separator}>·</span>
              <span className={cn(styles.label, styles.labelResolved)}>
                <CheckCircleOutlined aria-hidden className={styles.labelIcon} />
                {t("general.resolved")}
              </span>
            </>
          )}
        </Flex>
        <Flex wrap align="center" gap={10} className={styles.meta}>
          {isOpen ? (
            discoveredDate && (
              <span>
                {t("issue.statusBar.detectedOn", {
                  date: getDateInUserLocale(
                    discoveredDate,
                    DEFAULT_LONG_FORMAT
                  ),
                  count: getDiffToNow(discoveredDate)
                })}
              </span>
            )
          ) : (
            <DismissedMeta record={record} withStatus={withResolved} />
          )}
          {change && (
            <>
              <span className={styles.divider} />
              <Button
                type="link"
                size="small"
                aria-expanded={!collapsed}
                aria-controls={collapsed ? undefined : changeRowId}
                className={cn(
                  styles.toggle,
                  collapsed && styles.toggleCollapsed
                )}
                onClick={() => toggle()}
              >
                {t(
                  collapsed
                    ? "issue.statusBar.showChanges"
                    : "issue.statusBar.hideChanges"
                )}
              </Button>
            </>
          )}
        </Flex>
      </Flex>
      {withResolved && (
        <Flex
          className={cn(
            alertStyles.meta,
            alertStyles.metaResolveTheme,
            styles.indented
          )}
        >
          <ResolvedContent component="alert" record={record} t={t} />
        </Flex>
      )}
      {change && !collapsed && (
        <StatusChangeRow change={change} id={changeRowId} />
      )}
    </Flex>
  )
}
